import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Clock, ArrowRight, Search, ChevronLeft } from 'lucide-react';
import { Input } from '../components/ui/input';
import SEO from '../components/SEO';
import axios from 'axios';
import { trackPageView } from '../lib/track';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = '/couplein_logo.png';

// App Store Links
const IOS_LINK = 'https://apps.apple.com/us/app/couplein/id6749887412';
const ANDROID_LINK = 'https://play.google.com/store/apps/details?id=dev.expo.couplein&pcampaignid=web_share';

// Download Buttons Component
const DownloadButtons = ({ size = 'default', className = '' }) => (
  <div className={`flex flex-col sm:flex-row gap-3 ${className}`}>
    <a
      href={IOS_LINK}
      target="_blank"
      rel="noopener noreferrer"
      className={`flex items-center justify-center gap-2 bg-black text-white rounded-xl hover:bg-gray-800 transition-all hover:scale-105 ${size === 'large' ? 'px-6 py-4' : 'px-4 py-3'}`}
    >
      <svg className={size === 'large' ? 'w-8 h-8' : 'w-6 h-6'} viewBox="0 0 24 24" fill="currentColor">
        <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
      </svg>
      <div className="text-left">
        <p className="text-[10px] opacity-80">Download on the</p>
        <p className={`font-semibold ${size === 'large' ? 'text-lg' : 'text-sm'} -mt-1`}>App Store</p>
      </div>
    </a>
    <a
      href={ANDROID_LINK}
      target="_blank"
      rel="noopener noreferrer"
      className={`flex items-center justify-center gap-2 bg-black text-white rounded-xl hover:bg-gray-800 transition-all hover:scale-105 ${size === 'large' ? 'px-6 py-4' : 'px-4 py-3'}`}
    >
      <svg className={size === 'large' ? 'w-8 h-8' : 'w-6 h-6'} viewBox="0 0 24 24" fill="currentColor">
        <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
      </svg>
      <div className="text-left">
        <p className="text-[10px] opacity-80">Get it on</p>
        <p className={`font-semibold ${size === 'large' ? 'text-lg' : 'text-sm'} -mt-1`}>Google Play</p>
      </div>
    </a>
  </div>
);

import Navbar from '../components/Navbar';

// Blog Card Component
const BlogCard = ({ post }) => (
  <motion.article
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow group"
  >
    <Link to={`/${post.slug}`}>
      <div className="aspect-video bg-gradient-to-br from-pink-100 to-orange-100 overflow-hidden">
        {post.image ? (
          <img 
            src={post.image} 
            alt={post.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-6xl">
            {post.emoji || '💕'}
          </div>
        )}
      </div>
      <div className="p-6">
        {post.category && (
          <span className="inline-block bg-pink-100 text-pink-600 text-xs font-medium px-3 py-1 rounded-full mb-3">
            {post.category}
          </span>
        )}
        <h2 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-pink-500 transition-colors line-clamp-2">
          {post.title}
        </h2>
        <p className="text-gray-600 text-sm line-clamp-2 mb-4">{post.excerpt}</p>
        <div className="flex items-center justify-between text-sm text-gray-400">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {new Date(post.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {post.read_time || '5'} min read
            </span>
          </div>
          <ArrowRight className="w-5 h-5 text-pink-500 group-hover:translate-x-1 transition-transform" />
        </div>
      </div>
    </Link>
  </motion.article>
);

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  
  // Scroll inside #root via global CSS, no body/html overrides
  
  useEffect(() => {
    fetchPosts();
    trackPageView('blog');
  }, []);
  
  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/blog/posts`, { withCredentials: false });
      setPosts(response.data.posts || []);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Get unique categories
  const categories = ['all', ...new Set(posts.map(p => p.category).filter(Boolean))];
  
  // Filter posts
  const filteredPosts = posts.filter(post => {
    const q = searchQuery.toLowerCase().trim();
    const matchesSearch =
      !q ||
      post.title?.toLowerCase().includes(q) ||
      post.excerpt?.toLowerCase().includes(q) ||
      post.keywords?.toLowerCase().includes(q) ||
      post.category?.toLowerCase().includes(q) ||
      post.slug?.toLowerCase().includes(q);
    const matchesCategory = selectedCategory === 'all' || post.category === selectedCategory;
    return matchesSearch && matchesCategory && post.published;
  });
  
  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="CoupleIn Blog, Relationship Tips, Communication & Couple Goals"
        description="Practical relationship tips, communication tools, and couple stories, daily insights to help you build a stronger, happier partnership."
        path="/blog"
        keywords="relationship blog, couple tips, communication advice, conflict resolution, couple goals, brownie points, dating advice"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Blog',
          name: 'CoupleIn Blog',
          description: 'Tips, insights, and stories to help you build a stronger, happier relationship.',
          url: `${(process.env.REACT_APP_SITE_URL || 'https://coupleinapp.com').replace(/\/$/, '')}/blog`,
          publisher: { '@type': 'Organization', name: 'CoupleIn' },
          blogPost: posts.filter(p => p.published).slice(0, 10).map(p => ({
            '@type': 'BlogPosting',
            headline: p.title,
            url: `${(process.env.REACT_APP_SITE_URL || 'https://coupleinapp.com').replace(/\/$/, '')}/${p.slug}`,
            datePublished: p.created_at,
            dateModified: p.updated_at || p.created_at,
            image: p.image || undefined,
          })),
        }}
      />
      <Navbar />
      
      {/* Hero */}
      <section className="pt-24 pb-12 bg-gradient-to-br from-pink-50 to-orange-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/" className="inline-flex items-center gap-2 text-pink-500 hover:text-pink-600 mb-6">
            <ChevronLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            CoupleIn <span className="bg-gradient-to-r from-pink-500 to-orange-400 bg-clip-text text-transparent">Blog</span>
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl">
            Tips, insights, and stories to help you build a stronger, happier relationship.
          </p>
        </div>
      </section>
      
      {/* Search & Filter */}
      <section className="py-8 border-b border-gray-200 bg-white sticky top-16 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
            <div className="relative w-full sm:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex gap-2 overflow-x-auto pb-2 sm:pb-0 w-full sm:w-auto">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                    selectedCategory === cat
                      ? 'bg-pink-500 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {cat.charAt(0).toUpperCase() + cat.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>
      
      {/* Blog Posts */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-white rounded-2xl overflow-hidden shadow animate-pulse">
                  <div className="aspect-video bg-gray-200" />
                  <div className="p-6 space-y-3">
                    <div className="h-4 bg-gray-200 rounded w-20" />
                    <div className="h-6 bg-gray-200 rounded w-full" />
                    <div className="h-4 bg-gray-200 rounded w-3/4" />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredPosts.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map((post) => (
                <BlogCard key={post.id} post={post} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="text-6xl mb-4">📝</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No posts yet</h3>
              <p className="text-gray-600">Check back soon for relationship tips and insights!</p>
            </div>
          )}
        </div>
      </section>
      
      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-pink-500 to-orange-400">
        <div className="max-w-4xl mx-auto px-4 text-center text-white">
          <h2 className="text-3xl font-bold mb-4">Ready to strengthen your relationship?</h2>
          <p className="text-lg opacity-90 mb-8">Download CoupleIn and start growing closer today.</p>
          <DownloadButtons size="large" className="justify-center" />
        </div>
      </section>
      
      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 text-center text-gray-400 text-sm">
          <p>© {new Date().getFullYear()} CoupleIn. All rights reserved.</p>
          <div className="flex justify-center gap-4 mt-4">
            <Link to="/terms" className="hover:text-white">Terms</Link>
            <Link to="/privacy" className="hover:text-white">Privacy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Blog;